import React, { FC, useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";

import SupportAssistant from "./SupportAssistant";
import WalkthroughOverlay from "./WalkthroughOverlay";
import { useHasAcceptedTos } from "../../utility/useHasAcceptedTos";

/**
 * WHERE THE SUPPORT ASSISTANT LIVES, AND WHY IT IS NOT IN THE LAYOUT ANY MORE.
 *
 * ⚠️ THIS COMPONENT MUST BE A SIBLING OF `<Suspense>` IN `App.tsx`, NEVER A DESCENDANT.
 *
 * Every page is `React.lazy` behind ONE `<Suspense fallback={<Spinner/>}>` boundary. When the
 * user navigates to a route they have not visited this session, that page's chunk has to be
 * fetched — React suspends, and the fallback replaces the ENTIRE subtree under the boundary.
 * Anything mounted in there is unmounted: its state is gone, its effects are torn down, and it
 * is re-created from scratch when the chunk lands.
 *
 * The assistant used to be mounted inside that subtree, by `layouts/Default`. So clicking a
 * link the assistant itself had just offered destroyed the panel, the conversation in it, the
 * escalation state, and any walkthrough that was running. It worked on the SECOND visit to the
 * same screen, because `React.lazy` caches the resolved module and no longer suspends — which
 * is why this never presented as "navigation breaks the chat" and instead presented as the far
 * more confusing "open-from-chat sometimes loses my conversation". Half the feature this file
 * exists to support — chips that take you somewhere — would have been unusable, intermittently,
 * in a way no test that renders one screen could see.
 *
 * Mounted here, the panel and the walkthrough overlay outlive every navigation in the app.
 *
 * It still sits INSIDE `<AuthProvider>` and `<Router>`: the panel calls the BFF with the user's
 * token and reads `useLocation`/`useNavigate`.
 */

/**
 * The paths where the assistant is not offered.
 *
 * `/diagnostic` is the report form the assistant escalates INTO — offering "report this to
 * support" on top of the support form is noise. `/login` and `/logout` are the unauthenticated
 * screens: there is no session to ask about, and the panel could otherwise appear there because
 * the terms flag lives in `sessionStorage` and survives a logout in the same tab. That last one
 * is not a new rule, it is the old one made explicit — the previous mount was inside
 * `DefaultLayout`, which is inside `PrivateRoute`, so it could never render unauthenticated.
 */
const SUPPRESSED = ["/diagnostic/*", "/login", "/logout"];

const SupportAssistantHost: FC = () => {
  const location = useLocation();

  /**
   * The terms gate, previously owned by `layouts/Default`. A user who has not accepted them is
   * looking at the terms screen, not using the app.
   */
  const hasAcceptedTos = useHasAcceptedTos();

  const suppressed = useMemo(
    () =>
      SUPPRESSED.some((pattern) =>
        Boolean(matchPath({ path: pattern, end: false }, location.pathname))
      ),
    [location.pathname]
  );

  if (!hasAcceptedTos || suppressed) return null;

  return (
    <>
      {/* Renders NOTHING unless the SupportAssistantEnabled Key Vault flag is on, so both of
          these lines are inert in an environment without the secret. */}
      <SupportAssistant />

      {/* Self-driving: it subscribes to `utility/walkthroughRunner` and takes no props, which
          is what lets anything in the app start a walkthrough without being able to reach it.

          ⚠️ AND THAT IS WHAT LETS THE "?" LIVE SOMEWHERE ELSE. It used to be mounted here, next
          to the overlay it drives, because the host already knew the route and one mount covered
          all 29 screens without any page opting in. That argument was for a control that FLOATED
          — it had no parent it wanted to belong to, so the top of the app was as good a place as
          any. It is now a header control (`CSMShopfloorHeader`, beside Images/Documents/account/
          gear/About), and a header control is mounted by the header. Nothing was lost by moving
          it: the overlay is reached through the module-level bus, not through this tree, so the
          button still needs no ancestor in common with it.

          The route coverage is unchanged, which is worth stating because this host is the
          stricter of the two mounts. It suppresses on `/login`, `/logout`, `/diagnostic/*` and
          until the terms are accepted; `layouts/Default` renders this header only when the terms
          HAVE been accepted (the terms screen gets `TOSHeader`, a separate component with no "?")
          and never on `/diagnostic` (`showHeader = !hideHeader && !isDiagnostic`), and `/login`
          and `/logout` are `GuestLayout` routes that have no header at all. So the header can
          only render where the host already would, and the "?" cannot have appeared anywhere new.
          Its own `isSupportAssistantMounted()` gate then keeps it honest: a header that somehow
          rendered without this host would show no "?" rather than one that starts nothing. */}
      <WalkthroughOverlay />
    </>
  );
};

export default SupportAssistantHost;
